import {
  BranchesStockResponse,
  SupplierStockResponse,
  WheelModelDetails,
} from '@tyrio/dto';
import { DBService, DBServiceCategory } from '@prisma/client';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { POSContext } from './POSContext';
import { useGetBranchesStock } from '../features/pos/query/get-branches-stock';
import { useGetSuppliersStock } from '../features/pos/query/get-suppliers-stock';
import { useGetServices } from '../features/pos/query/get-services';
import { usePosCartData } from '../features/pos/steps/Cart/helpers/cart-data';
import { useGetWheelDetails } from '../features/pos/components/WheelsSearch/query/get-wheel-details';
import { useVehicleCtx } from './VehicleContext';

export interface BadgeContent {
  BRANCHES: number;
  SUPPLIERS: number;
}

export interface IServiceItems {
  services: DBService[];
  recommended: (DBService & { serviceCategory: DBServiceCategory })[];
}
type PosDetailsContextInterface = {
  branchesStock: BranchesStockResponse[];
  suppliersStockItems: SupplierStockResponse[];
  servicesItems: IServiceItems;
  upcomingStockItems: BranchesStockResponse[];
  onStockItems: BranchesStockResponse[];
  searchService: string;
  setSearchService: (a: string) => void;
  badgeContent: BadgeContent;
  wheelDetailsContext: WheelModelDetails[];
  setIsWheels: (a: boolean) => void;
  showWheelsLoader: boolean;
  showBranchesLoader: boolean;
  showLoaderSupplier: boolean;
};

export const POSDetailsContext =
  React.createContext<PosDetailsContextInterface>({
    branchesStock: [],
    suppliersStockItems: [],
    servicesItems: { services: [], recommended: [] },
    upcomingStockItems: [],
    onStockItems: [],
    searchService: '',
    setSearchService: () => null,
    badgeContent: { BRANCHES: 0, SUPPLIERS: 0 },
    wheelDetailsContext: [],
    setIsWheels: () => null,
    showWheelsLoader: false,
    showBranchesLoader: false,
    showLoaderSupplier: false,
  });

interface PosDetailsProviderProps {
  children: React.ReactNode;
}

export const PosDetailsProvider = ({ children }: PosDetailsProviderProps) => {
  const {
    selectedTableRow,
    selectedWarehouseId,
    shouldShowDot,
    filterValues,
    shouldRefetch,
    isSearchDiscarded,
    setShouldRefetch,
  } = useContext(POSContext);
  const { modalData } = usePosCartData();
  const [searchService, setSearchService] = useState<string>('');

  const [isWheels, setIsWheels] = useState<boolean>(false);

  const { selectedWheel, setSelectedProductModel, apiData, selectedProduct } =
    useVehicleCtx();

  const ean = isWheels ? selectedProduct?.ean : selectedTableRow?.product.ean;
  const eansForServices =
    modalData.length > 0 ? modalData.map((item) => item.req.ean).join(',') : '';
  const stockType = isWheels ? 'CURRENT' : selectedTableRow?.stockType;
  const {
    warehouseStock,
    upcomingStock,
    onStock,
    showLoader: showBranchesLoader,
  } = useGetBranchesStock(
    ean ?? '',
    selectedWarehouseId?.toString(),
    stockType,
    shouldShowDot,
    filterValues['customer']?.toString() ?? 'default-customer',
    shouldRefetch
  );

  const {
    suppliersStock: supplierStockResponse,
    showLoader: showLoaderSupplier,
  } = useGetSuppliersStock(
    ean ?? '',
    'Price ascending',
    selectedWarehouseId?.toString() as string,
    filterValues['customer']?.toString() ?? 'default-customer',
    undefined,
    undefined
  );
  const { recommendedServices, services } = useGetServices(
    eansForServices as string,
    (selectedWarehouseId as number) ?? 1,
    searchService,
    modalData
  );

  const { data: wheelDetails, showLoader: showWheelsLoader } =
    useGetWheelDetails(
      selectedWheel?.model.modelName ?? '',
      selectedWheel?.model.brand ?? '',
      shouldRefetch && !!selectedWheel,
      setSelectedProductModel,
      selectedWheel?.model?.uid,
      apiData?.pcd,
      selectedWheel?.model?.color,
      apiData?.et ?? [],
      apiData?.rimDiameter ?? [],
      apiData?.cb ?? ''
    );

  const suppliersStockItems = useMemo(() => {
    if (isSearchDiscarded) return [];
    if (supplierStockResponse) return supplierStockResponse;
    else return [];
  }, [supplierStockResponse, isSearchDiscarded]);

  const onStockItems = useMemo(() => {
    return isSearchDiscarded ? [] : onStock ? onStock : [];
  }, [onStock, isSearchDiscarded]);

  const upcomingStockItems = useMemo(() => {
    return isSearchDiscarded ? [] : upcomingStock ? upcomingStock : [];
  }, [upcomingStock, isSearchDiscarded]);

  const branchesStock = useMemo(() => {
    return isSearchDiscarded ? [] : warehouseStock ? warehouseStock : [];
  }, [warehouseStock, isSearchDiscarded]);

  const badgeContent = useMemo(() => {
    if (isSearchDiscarded) {
      return { BRANCHES: 0, SUPPLIERS: 0 };
    }

    const calculateSum = (stock: any[]) =>
      stock?.reduce((total, record) => {
        const itemSum = record?.lineItems?.reduce(
          (sum: number, item: any) => sum + (item.quantity - item.reserved),
          0
        );
        return total + (itemSum || 0);
      }, 0) || 0;

    const branchSum = calculateSum(warehouseStock ?? []);
    const supplierSum = calculateSum(supplierStockResponse);

    return {
      BRANCHES: branchSum,
      SUPPLIERS: supplierSum,
    };
  }, [isSearchDiscarded, warehouseStock, supplierStockResponse]);

  useEffect(() => {
    setShouldRefetch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowDot, selectedWarehouseId]);

  const wheelDetailsContext = useMemo(() => {
    return isSearchDiscarded ? [] : wheelDetails ? wheelDetails : [];
  }, [wheelDetails, isSearchDiscarded]);

  const servicesItems = useMemo(() => {
    return {
      recommended: recommendedServices ?? [],
      services: services ?? [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedServices, services]);

  return (
    <POSDetailsContext.Provider
      value={{
        branchesStock,
        suppliersStockItems,
        servicesItems,
        upcomingStockItems,
        onStockItems,
        searchService,
        setSearchService,
        badgeContent,
        wheelDetailsContext,
        setIsWheels,
        showWheelsLoader,
        showBranchesLoader,
        showLoaderSupplier,
      }}
    >
      {children}
    </POSDetailsContext.Provider>
  );
};
